import {
  distanceMeters,
  formatMetricDistance,
} from "../../lib/geo/distance.ts";
import type { LngLat } from "../../lib/geo/types.ts";
import type { OsmElementType } from "../../lib/osm.ts";
import type { OsmSelectionTarget } from "../MapScreen/selection/osmLookup.ts";
import type { SearchIconName } from "../../lib/search/searchIcons.ts";
import type { PhotonFeature, PhotonMainClassification } from "./photon.ts";

interface PhotonOsmIdentity {
  type: OsmElementType;
  id: number;
}

export interface SearchResultLabel {
  title: string;
  subtitle: string | undefined;
}

export function iconForPhotonClassification(
  classification: PhotonMainClassification,
): SearchIconName {
  switch (`${classification.key}=${classification.value}`) {
    case "place=city":
    case "place=town":
    case "place=village":
      return "Building2";
    case "railway=station":
    case "railway=halt":
    case "railway=stop":
      return "TrainFront";
    case "highway=bus_stop":
      return "BusFront";
    case "amenity=library":
      return "LibraryBig";
    case "amenity=cafe":
      return "Coffee";
    case "amenity=pub":
    case "amenity=bar":
      return "Beer";
    case "amenity=restaurant":
      return "Utensils";
    case "amenity=university":
      return "GraduationCap";
    case "amenity=hospital":
      return "Hospital";
    case "amenity=ice_cream":
      return "IceCreamCone";
    case "amenity=fast_food":
      return "Hamburger";
    case "amenity=school":
      return "GraduationCap";
    case "healthcare=alternative":
      return "Stethoscope";
    case "historic=memorial":
      return "Landmark";
    case "leisure=fitness_centre":
    case "leisure=sports_centre":
      return "Dumbbell";
    case "leisure=playground":
      return "Castle";
    case "leisure=park":
      return "Trees";
    case "office=company":
    case "office=estate_agent":
      return "BriefcaseBusiness";
    case "place=square":
      return "Square";
    case "tourism=viewpoint":
      return "Binoculars";
    case "tourism=museum":
      return "Landmark";
    case "tourism=hotel":
    case "tourism=guest_house":
      return "Hotel";
    case "shop=supermarket":
      return "ShoppingCart";
    case "shop=convenience":
    case "shop=department_store":
      return "ShoppingBasket";
    case "shop=hairdresser":
      return "Scissors";
    case "shop=bakery":
      return "Croissant";
    case "shop=ice_cream":
      return "IceCreamCone";
    case "shop=clothes":
      return "Shirt";
    default:
      return "Search";
  }
}

export function formatSearchResultLabel(
  feature: PhotonFeature,
  userLngLat: LngLat | undefined,
): SearchResultLabel {
  const p = feature.properties;
  const streetAddr = [p.street, p.housenumber].filter(Boolean).join(" ");
  const title = p.name ?? streetAddr;

  const detailParts: string[] = [];
  if (p.name && streetAddr) detailParts.push(streetAddr);
  if (p.postcode) detailParts.push(p.postcode);
  if (p.city && p.city !== p.name) detailParts.push(p.city);
  if (p.country) detailParts.push(p.country);

  return {
    title,
    subtitle: joinSubtitle(
      formatDistanceFromUser(feature, userLngLat),
      detailParts.join(", "),
    ),
  };
}

function photonFeatureLngLat(feature: PhotonFeature): LngLat | undefined {
  const lng = feature.geometry.coordinates[0];
  const lat = feature.geometry.coordinates[1];
  return lng === undefined || lat === undefined ? undefined : [lng, lat];
}

export function photonFeatureSelectionTarget(
  feature: PhotonFeature,
): OsmSelectionTarget {
  const lngLat = photonFeatureLngLat(feature);
  if (!lngLat) {
    throw new Error("Photon feature is missing point coordinates.");
  }

  const identity = photonOsmIdentity(feature);
  return identity
    ? { kind: "element", ...identity, lngLat }
    : { kind: "nearby", lngLat, nameHint: feature.properties.name };
}

export function photonOsmIdentity(
  feature: PhotonFeature,
): PhotonOsmIdentity | undefined {
  const type = parsePhotonOsmType(feature.properties.osm_type);
  const id = parsePhotonOsmId(feature.properties.osm_id);
  return type && id !== undefined ? { type, id } : undefined;
}

function formatDistanceFromUser(
  feature: PhotonFeature,
  userLngLat: LngLat | undefined,
): string | undefined {
  const featureLngLat = photonFeatureLngLat(feature);
  if (!userLngLat || !featureLngLat) return undefined;

  return formatMetricDistance(distanceMeters(userLngLat, featureLngLat));
}

function joinSubtitle(
  distance: string | undefined,
  detail: string,
): string | undefined {
  if (distance && detail) return `${distance} · ${detail}`;
  return distance || detail || undefined;
}

function parsePhotonOsmType(
  value: string | undefined,
): OsmElementType | undefined {
  switch (value) {
    case "N":
      return "node";
    case "W":
      return "way";
    case "R":
      return "relation";
    default:
      return undefined;
  }
}

function parsePhotonOsmId(value: number | undefined): number | undefined {
  return value !== undefined && Number.isSafeInteger(value) && value > 0
    ? value
    : undefined;
}
